import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../../api/apiClient";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { domain, token } from "../../Security";
import { SetAuditTrail } from "../../Reports/AuditTrailReportEmployeeSetup";
import loadingSpinner from "../../../assets/loading.gif";
import IntroImg from "../../../assets/payroll-intro.jpeg";
import { BarChart, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { TypingAnimation } from "./TypingAnimation";
import { CustomTooltip } from "./CustomTooltip";
import { EmployeeMissingRequirements } from "./EmployeeMissingRequirements";
import { MemoModal } from "../../../common/MemoFld/MemoModal";
import { AnnouncementModal } from "../../../common/MemoFld/AnnouncementModal";
import { EmployeeMemoModal } from "../../../common/MemoFld/EmployeeMemoModal";
import { GeneralMessage, useGeneralMessage } from "../../../common/APICommon";
import { LoadingSpinner } from "../../../common/Loading";

export function ShowIntroduction({
  filteredEmployee,
  setEmployeePhoto,
  employees,
  user,
  userAccess,
}) {
  const [currentView, setCurrentView] = useState("announcement");
  const [imageSource, setImageSource] = useState([]);
  const announcementRef = useRef(null);
  const memoRef = useRef(null);
  const todayAnnouncementRef = useRef(null);
  const todayMemoRef = useRef(null);

  const [employeeInfo, setEmployeeInfo] = useState([]);
  const [generalInfo, setGeneralInfo] = useState([]);
  const [events, setEvents] = useState([]);
  const [todayAnnouncementEvents, setTodayAnnouncementEvents] = useState([]);
  const [todayMemoEvents, setTodayMemoEvents] = useState([]);
  const [todayEmployeeMemoEvents, setTodayEmployeeMemoEvents] = useState([]);

  const [selectedFile, setSelectedFile] = useState("");
  const [loadingRequest, setLoadingRequest] = useState(false);

  const [hasGeneralAnnouncement, setHasGeneralAnnouncement] = useState(false);
  const [hasGeneralMemo, setHasGeneralMemo] = useState(false);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageSrcModalOpen, setImageSrcModalOpen] = useState("");

  // ─── UNIFIED MODAL QUEUE ───────────────────────────────────────────────────
  const [modalQueue, setModalQueue] = useState([]);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);

  // Build queue once all today's events are fetched
  useEffect(() => {
    const combined = [
      ...todayAnnouncementEvents.map((e) => ({ type: "announcement", ...e })),
      ...todayMemoEvents.map((e) => ({ type: "memo", ...e })),
      ...todayEmployeeMemoEvents.map((e) => ({ type: "employeeMemo", ...e })),
    ];
    setModalQueue(combined);
    setCurrentModalIndex(0);
  }, [todayAnnouncementEvents, todayMemoEvents, todayEmployeeMemoEvents]);

  const handleCloseModal = () => {
    setCurrentModalIndex((prev) => prev + 1);
  };

  const currentModal = modalQueue[currentModalIndex] ?? null;
  // ──────────────────────────────────────────────────────────────────────────

  const openImage = (imageSrc) => {
    setIsImageModalOpen(true);
    setImageSrcModalOpen(imageSrc);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const {
    showMessage,
    message: apiMessage,
    isSuccess: apiIsSuccess,
    showGeneralMessage,
  } = useGeneralMessage(5000);

  const apiUrl = domain + "/api/General";

  const fetchData = useCallback(async () => {
    await axios
      .get(`${apiUrl}/totals`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setGeneralInfo(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [apiUrl]);

  const fetchDataEmployeeInfo = useCallback(async () => {
    const apiUrl = domain + "/api/Employee";
    await axios
      .get(`${apiUrl}/${user.employeePersonalInformationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setEmployeeInfo(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [user]);

  const fetchDataTodayAnnouncementEvents = useCallback(async () => {
    const apiUrl = domain + "/api/Announcement";
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      let filteredData = [];
      response.data.forEach((announcement) => {
        const announcementDateFrom = new Date(announcement.dateFrom);
        const announcementDateTo = new Date(announcement.dateTo);
        const thisDate = new Date();
        const thisDateOnly = new Date(thisDate.setHours(0, 0, 0, 0));
        const announcementDateFromOnly = new Date(
          announcementDateFrom.setHours(0, 0, 0, 0)
        );
        const announcementDateToOnly = new Date(
          announcementDateTo.setHours(23, 59, 59, 999)
        );
        if (
          thisDateOnly >= announcementDateFromOnly &&
          thisDateOnly <= announcementDateToOnly
        ) {
          filteredData.push(announcement);
        }
      });
      return filteredData;
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  }, []);

  const fetchDataTodayMemoEvents = useCallback(async () => {
    const apiUrl = domain + "/api/Memo";
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      let filteredData = [];
      response.data.forEach((memo) => {
        const memoDateFrom = new Date(memo.dateFrom);
        const memoDateTo = new Date(memo.dateTo);
        const thisDate = new Date();
        const thisDateOnly = new Date(thisDate.setHours(0, 0, 0, 0));
        const memoDateFromOnly = new Date(memoDateFrom.setHours(0, 0, 0, 0));
        const memoDateToOnly = new Date(memoDateTo.setHours(23, 59, 59, 999));
        if (
          thisDateOnly >= memoDateFromOnly &&
          thisDateOnly <= memoDateToOnly
        ) {
          filteredData.push(memo);
        }
      });
      return filteredData;
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  }, []);

  const fetchDataTodayEmployeeMemoEvents = useCallback(async () => {
    // PATCHED: use employee-specific endpoint — server returns primary + CC memos, each with isCc flag
    const apiUrl = domain + `/api/EmployeeMemo/employee/${user.employeePersonalInformationId}/active`;
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      let filteredData = [];
      response.data.forEach((memo) => {
        const memoDateFrom = new Date(memo.dateFrom);
        const memoDateTo = new Date(memo.dateTo);
        const thisDate = new Date();
        const thisDateOnly = new Date(thisDate.setHours(0, 0, 0, 0));
        const memoDateFromOnly = new Date(memoDateFrom.setHours(0, 0, 0, 0));
        const memoDateToOnly = new Date(memoDateTo.setHours(23, 59, 59, 999));
        if (
          thisDateOnly >= memoDateFromOnly &&
          thisDateOnly <= memoDateToOnly
        ) {
          filteredData.push(memo);
        }
      });
      return filteredData;
    } catch (error) {
      console.error("Error fetching employee memos:", error);
      return [];
    }
  }, [user.employeePersonalInformationId]);

  const fetchDataEvents = useCallback(async () => {
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const _announcements = await fetchDataTodayAnnouncementEvents();
      const _memos = await fetchDataTodayMemoEvents();
      const _employeeMemos = await fetchDataTodayEmployeeMemoEvents();

      setTodayAnnouncementEvents(_announcements);
      setTodayMemoEvents(_memos);
      setTodayEmployeeMemoEvents(_employeeMemos);
      setEvents(response.data);

      const findAnnouncement = response.data.find(
        (event) => event.key === "Announcement"
      );
      const findMemo = response.data.find((event) => event.key === "Memo");

      setHasGeneralAnnouncement(!!findAnnouncement);
      setHasGeneralMemo(!!findMemo);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [
    apiUrl,
    fetchDataTodayAnnouncementEvents,
    fetchDataTodayMemoEvents,
    fetchDataTodayEmployeeMemoEvents,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchDataEmployeeInfo();
  }, [fetchDataEmployeeInfo]);

  useEffect(() => {
    fetchDataEvents();
  }, [fetchDataEvents]);

  useEffect(() => {
    if (employeeInfo && employeeInfo.photo) {
      const binaryData = atob(employeeInfo.photo);
      const arrayBuffer = new ArrayBuffer(binaryData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: "image/jpeg" });
      const file = new File([blob], "", { type: "image/jpeg" });
      setSelectedFile(file);
    }
  }, [employeeInfo, employees, user]);

  const barData = [
    {
      name: "Leave Request",
      amount: generalInfo.totalLeaveRequests,
      fill: "#ff4d4d",
    },
    {
      name: "OT Request",
      amount: generalInfo.totalOTRequests,
      fill: "#47d147",
    },
    {
      name: "Loan Request",
      amount: generalInfo.totalLoanRequests,
      fill: "#1a75ff",
    },
  ];

  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { month: "long", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  const handleUpdate = async (event, file) => {
    const apiUrlUpdate = domain + "/api/Employee";
    event.preventDefault();
    setLoadingRequest(true);
    const now = new Date();

    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

    let base64String = "";
    let photoToBeSave = "";

    if (file) {
      base64String = await toBase64(file);
      photoToBeSave = base64String.split(",")[1];
    }

    const data = {
      employeeId: employeeInfo.employeeId,
      lastName: employeeInfo.lastName,
      firstName: employeeInfo.firstName,
      middleName: employeeInfo.middleName,
      extensionName: employeeInfo.extensionName,
      fullName: employeeInfo.fullName,
      nickName: employeeInfo.nickName,
      age: employeeInfo.age ? employeeInfo.age : 0,
      dateOfBirth: employeeInfo.dateOfBirth ? employeeInfo.dateOfBirth : null,
      placeOfBirth: employeeInfo.placeOfBirth,
      civilStatus: employeeInfo.civilStatus,
      bloodType: employeeInfo.bloodType,
      height: employeeInfo.height,
      weight: employeeInfo.weight,
      citizenship: employeeInfo.citizenship,
      religion: employeeInfo.religion,
      gender: employeeInfo.gender,
      mobileNumber: employeeInfo.mobileNumber,
      emailAddress: employeeInfo.emailAddress,
      blkLot: employeeInfo.blkLot,
      street: employeeInfo.street,
      barangay: employeeInfo.barangay,
      city: employeeInfo.city,
      postalCode: employeeInfo.postalCode,
      yearsOfStay: employeeInfo.yearsOfStay ? employeeInfo.yearsOfStay : 0,
      photo: photoToBeSave,
      deviceNumber: employeeInfo.deviceNumber,
      employeeInformation: {
        employeePersonalInformationId: employeeInfo.id,
        locationId: employeeInfo.employeeInformation.locationId ?? null,
        dateEmployed: employeeInfo.employeeInformation.dateEmployed ?? null,
        dateRegular: employeeInfo.employeeInformation.dateRegular ?? null,
        dateProbationary: employeeInfo.employeeInformation.dateProbationary ?? null,
        dateProjectBase: employeeInfo.employeeInformation.dateProjectBase ?? null,
        dateContractual: employeeInfo.employeeInformation.dateContractual ?? null,
        employmentId: employeeInfo.employeeInformation.employmentId ?? null,
        departmentId: employeeInfo.employeeInformation.departmentId ?? null,
        sectionId: employeeInfo.employeeInformation.sectionId ?? null,
        classificationId: employeeInfo.employeeInformation.classificationId ?? null,
        payTypeId: employeeInfo.employeeInformation.payTypeId ?? null,
        shiftScheduleId: employeeInfo.employeeInformation.shiftScheduleId ?? null,
        positionId: employeeInfo.employeeInformation.positionId ?? null,
        companyId: employeeInfo.employeeInformation.companyId ?? null,
        salaryType: employeeInfo.employeeInformation.salaryType ?? null,
        createdDate: now,
        createdBy: 0,
      },
      employeeReportTo: {
        employeePersonalInformationId: employeeInfo.id,
        reportToId: null,
      },
      employeeAccountInformation: {
        employeePersonalInformationId: employeeInfo.id,
        sss: employeeInfo.employeeAccountInformation.sss,
        tin: employeeInfo.employeeAccountInformation.tin,
        passport: employeeInfo.employeeAccountInformation.passport,
        philhealth: employeeInfo.employeeAccountInformation.philhealth,
        pagibig: employeeInfo.employeeAccountInformation.pagibig,
      },
      employeeEducationAttainments: employeeInfo.employeeEducationAttainments,
      employeeDependents: employeeInfo.employeeDependents,
      employeeFamilyData: employeeInfo.employeeFamilyData,
      employeeEmergencyContacts: employeeInfo.employeeEmergencyContacts,
      employeeLeave: {
        employeePersonalInformationId: employeeInfo.id,
        totalVacation: employeeInfo.employeeLeave?.totalVacation ?? 0,
        vacationRemaining: employeeInfo.employeeLeave?.vacationRemaining ?? 0,
        totalSick: employeeInfo.employeeLeave?.totalSick ?? 0,
        sickRemaining: employeeInfo.employeeLeave?.sickRemaining ?? 0,
        totalServiceIncentive: employeeInfo.employeeLeave?.totalServiceIncentive ?? 0,
        serviceIncentiveRemaining: employeeInfo.employeeLeave?.serviceIncentiveRemaining ?? 0,
        totalSoloParent: employeeInfo.employeeLeave?.totalSoloParent ?? 0,
        soloParentRemaining: employeeInfo.employeeLeave?.soloParentRemaining ?? 0,
      },
      employeeStatuses: {
        employeePersonalInformationId: employeeInfo.id,
        status: employeeInfo.employeeStatuses?.status ?? null,
      },
    };

    try {
      await axios.put(apiUrlUpdate + `/${employeeInfo.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await SetAuditTrail({
        setupArea: "Home",
        actionTaken: "update",
        field: "Employee's Photo",
        newValue: "-",
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id,
      });

      showGeneralMessage("Request Success!", true);

      if (data.photo) {
        const binaryData = atob(data.photo);
        const arrayBuffer = new ArrayBuffer(binaryData.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < binaryData.length; i++) {
          uint8Array[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([uint8Array], { type: "image/jpeg" });
        const file = new File([blob], "filename.jpg", { type: "image/jpeg" });
        setEmployeePhoto(file);
        setSelectedFile(file);
      }

      console.log("Data updated successfully");
    } catch (error) {
      setLoadingRequest(false);
      console.error("There was a problem updating data:", error);
      const errorMessage = JSON.parse(
        error.response.request.responseText
      ).message;
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      handleUpdate(event, file);
    } else {
      alert("Please select an image file.");
    }
  };

  const getGeneralImageSource = useCallback(
    (value) => {
      let imageSrc = [];
      const findEvent = events.find((event) => event.key === value);
      const findAllEvent = events.filter(
        (event) => event.key === "Announcement" || event.key === "Memo"
      );

      if (findEvent && findEvent.content) {
        imageSrc.push("data:image/jpeg;base64," + findEvent.content);
      }

      const isThereAnImage = findAllEvent.some((event) => event.content);
      if (!isThereAnImage) {
        imageSrc.push(IntroImg);
      }

      return imageSrc;
    },
    [events]
  );

  const announcementContent = () => {
    const findAnnouncement = events.find(
      (event) => event.key === "Announcement"
    );
    if (findAnnouncement) {
      const formattedDescription = findAnnouncement.description
        .split("\n")
        .map((line, index) => (
          <p key={index} className="mb-2 leading-relaxed">
            {line}
          </p>
        ));
      return (
        <div className="bg-white rounded-2xl p-6 max-w-xl mx-auto border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-500 mb-4">
            📢 Announcement
          </h2>
          <div className="text-gray-500 space-y-2">{formattedDescription}</div>
        </div>
      );
    }
    return null;
  };

  const memoContent = () => {
    const findMemo = events.find((event) => event.key === "Memo");
    if (findMemo) {
      const formattedDescription = findMemo.description
        .split("\n")
        .map((line, index) => (
          <p key={index} className="mb-2 leading-relaxed">
            {line}
          </p>
        ));
      return (
        <div className="bg-white rounded-2xl p-6 max-w-xl mx-auto border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-500 mb-4">
            📝 Memo
          </h2>
          <div className="text-gray-500 space-y-2">{formattedDescription}</div>
        </div>
      );
    }
    return null;
  };

  function areDatesEqual(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  const getImageSource = useCallback(
    (value) => {
      let eventsToFilter = [];
      if (value === "TodayAnnouncement") {
        eventsToFilter = todayAnnouncementEvents;
      } else if (value === "TodayMemo") {
        eventsToFilter = todayMemoEvents;
      }

      const findEvent = eventsToFilter.filter((event) =>
        areDatesEqual(event.date, new Date())
      );

      if (findEvent.length > 0) {
        return findEvent
          .filter((event) => event.content)
          .map((event) => "data:image/jpeg;base64," + event.content);
      }

      return [];
    },
    [todayAnnouncementEvents, todayMemoEvents]
  );

  useEffect(() => {
    if (events.length > 0) {
      const announcement = announcementRef.current;
      const memo = memoRef.current;
      const todayAnnouncement = todayAnnouncementRef.current;
      const todayMemo = todayMemoRef.current;

      const observerOptions = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentView(entry.target.id);
          }
        });
      }, observerOptions);

      if (announcement) observer.observe(announcement);
      if (memo) observer.observe(memo);
      if (todayAnnouncement) observer.observe(todayAnnouncement);
      if (todayMemo) observer.observe(todayMemo);

      return () => {
        if (announcement) observer.unobserve(announcement);
        if (memo) observer.unobserve(memo);
        if (todayAnnouncement) observer.unobserve(todayAnnouncement);
        if (todayMemo) observer.unobserve(todayMemo);
      };
    }
  }, [events]);

  useEffect(() => {
    if (events.length > 0) {
      if (currentView === "announcement") {
        setCurrentIndex(0);
        if (getGeneralImageSource("Announcement").length > 0) {
          setImageSource(getGeneralImageSource("Announcement"));
        }
      } else if (currentView === "memo") {
        setCurrentIndex(0);
        if (getGeneralImageSource("Memo").length > 0) {
          setImageSource(getGeneralImageSource("Memo"));
        }
      } else if (currentView === "today-announcement") {
        setCurrentIndex(0);
        if (getImageSource("TodayAnnouncement").length > 0) {
          setImageSource(getImageSource("TodayAnnouncement"));
        }
      } else if (currentView === "today-memo") {
        setCurrentIndex(0);
        if (getImageSource("TodayMemo").length > 0) {
          setImageSource(getImageSource("TodayMemo"));
        }
      }
    }
  }, [currentView, getGeneralImageSource, getImageSource, events]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % imageSource.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + imageSource.length) % imageSource.length
    );
  };

  return (
    <>
      <div
        className={`Introduction row flex flex-col gap-2 w-full ${
          userAccess?.hasHome ? "" : "2xl:flex-row"
        }`}
      >
        <div className="!w-full 2xl:!w-full 2xl:!w-2/3 dashboard row shadow-md">
          {events.length > 0 ? (
            <>
              <div className="Intro-img">
                {imageSource.length > 0 && (
                  <img
                    className="modal-events-image"
                    src={imageSource[currentIndex]}
                    alt=""
                    onClick={() => openImage(imageSource[currentIndex])}
                  />
                )}
                {imageSource.length > 1 && (
                  <div className="imageButton">
                    <button onClick={handlePrev}>{`<`}</button>
                    <button onClick={handleNext}>{`>`}</button>
                  </div>
                )}
              </div>
              <div className="Intro-content">
                <div className="contentText">
                  <h2 style={{ margin: "10px" }}>
                    {
                      <TypingAnimation
                        wordings={
                          "Welcome Back " +
                          user.employeePersonalInformationFullName
                        }
                      />
                    }
                  </h2>
                  {hasGeneralAnnouncement && (
                    <div id="announcement" ref={announcementRef}>
                      {announcementContent()}
                    </div>
                  )}
                  {hasGeneralMemo && (
                    <div id="memo" ref={memoRef}>
                      {memoContent()}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <img src={loadingSpinner} alt="Loading..." />
            </div>
          )}
        </div>

        {userAccess?.hasHome ? (
          <div className="flex flex-col xl:flex-row gap-2 w-full">
            <div className="!w-full xl:!w-1/2 flex flex-col md:flex-row bg-white shadow-md rounded-md h-[250px] gap-2 overflow-hidden">
              <div className="flex flex-col items-center justify-center rounded-xl p-2 w-full md:w-1/3">
                <h3 className="text-lg font-semibold text-gray-500 text-center">
                  Total Employees
                </h3>
                <h1 className="text-4xl font-bold text-customMainColor mt-2">
                  {generalInfo.totalEmployees}
                </h1>
              </div>
              <div className="flex flex-col gap-4 w-full md:w-2/3">
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="amount"
                        fill="#00a0fc"
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-around text-center text-xs font-medium text-gray-500 gap-1 p-2">
                  <div className="px-3 py-2 bg-gray-100 rounded-lg shadow-sm">
                    <b>Leave Request</b>
                  </div>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg shadow-sm">
                    <b>OT Request</b>
                  </div>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg shadow-sm">
                    <b>Loan Request</b>
                  </div>
                </div>
              </div>
            </div>
            <EmployeeMissingRequirements />
          </div>
        ) : (
          <div className="w-full 2xl:w-1/3 bg-white shadow-md rounded-md flex flex-col overflow-hidden h-[250px]">
            <div className="flex items-center gap-2 bg-customMainColor text-white px-4 py-3">
              <AssignmentIndIcon className="text-white" />
              <span className="text-lg font-semibold tracking-wide">
                Your Profile
              </span>
            </div>
            <div className="flex flex-col md:flex-row p-4 gap-6">
              {filteredEmployee ? (
                <>
                  <div className="flex flex-col items-center justify-center text-gray-500 w-full md:w-1/3">
                    <label
                      htmlFor="file-input"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <img
                        src={
                          selectedFile
                            ? URL.createObjectURL(selectedFile)
                            : "images/sample.jpg"
                        }
                        alt="profile"
                        className="w-28 h-28 object-cover rounded-full shadow-md border border-gray-200"
                      />
                      {!selectedFile && (
                        <span className="mt-2 text-xs text-gray-500">
                          Upload a picture
                        </span>
                      )}
                    </label>
                    <input
                      id="file-input"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  <div className="flex-1 w-full md:w-2/3">
                    <div className="space-y-3 text-gray-600">
                      <p>
                        <span className="font-semibold text-gray-500">
                          Name:{" "}
                        </span>
                        {filteredEmployee.fullName}
                      </p>
                      <p>
                        <span className="font-semibold text-gray-500">
                          Department:{" "}
                        </span>
                        {filteredEmployee.employeeInformation
                          ?.departmentDescription ?? ""}
                      </p>
                      <p>
                        <span className="font-semibold text-gray-500">
                          Position:{" "}
                        </span>
                        {filteredEmployee.employeeInformation
                          ?.positionDescription ?? ""}
                      </p>
                      <p>
                        <span className="font-semibold text-gray-500">
                          Date Employed:{" "}
                        </span>
                        {filteredEmployee.employeeInformation?.dateEmployed
                          ? formatDate(
                              filteredEmployee.employeeInformation.dateEmployed
                            )
                          : ""}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center w-full h-48">
                  <img
                    src={loadingSpinner}
                    alt="Loading..."
                    className="w-8 h-8"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Full Image Modal */}
      {isImageModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={closeImageModal}
        >
          <img
            src={imageSrcModalOpen}
            alt="Full view"
            style={{ maxWidth: "90%", maxHeight: "90%" }}
          />
        </div>
      )}

      {/* ─── UNIFIED MODAL QUEUE ─────────────────────────────────────────────── */}
      {currentModal?.type === "announcement" && (
        <AnnouncementModal
          isOpen={true}
          onClose={handleCloseModal}
          content={currentModal.description}
          image={currentModal.content}
        />
      )}

      {currentModal?.type === "memo" && (
        <MemoModal
          isOpen={true}
          onClose={handleCloseModal}
          content={currentModal.description}
          image={currentModal.content}
        />
      )}

      {currentModal?.type === "employeeMemo" && (
        <EmployeeMemoModal
          isOpen={true}
          onClose={handleCloseModal}
          title={currentModal.memoTypeName ?? currentModal.title ?? "Employee Memo"}
          content={currentModal.description}
          image={currentModal.content}
          isCc={currentModal.isCc ?? false}
        />
      )}
      {/* ─────────────────────────────────────────────────────────────────────── */}

      <GeneralMessage
        form={showMessage}
        message={apiMessage}
        isSuccess={apiIsSuccess}
        duration={5000}
      />

      <LoadingSpinner loading={loadingRequest} />
    </>
  );
}